import { useEffect, useState } from "react";

import { getSession } from "next-auth/react";
import { Tab } from "@headlessui/react";
import { ref } from "firebase/database";
import { useList } from "react-firebase-hooks/database";

import NoTextNavBackground from "../../components/common/NoTextNavBackground";
import { apiWrapper } from "../../utils/api/apiWrapper";
import { kLiveMatchDetailURL } from "../../utils/constants/apiUrls";
import {
  ADDED_TIME,
  FIRST_EXTRA_TIME,
  FIRST_HALF_KICK_OFF,
  FULL_TIME,
  GUEST_TOKEN,
  HALF_TIME,
  IMAGE_POST,
  kAT,
  kET,
  kFT,
  kHT,
  KOTOKO_GOAL,
  LIVE_MATCH_COMMENTARY_PATH,
  LIVE_MATCH_FIXTURES_PATH,
  LIVE_MATCH_FULL_TIME_STATUS,
  LIVE_MATCH_GOAL_EVENT,
  LIVE_MATCH_LINE_UPS_PATH,
  LIVE_MATCH_PENALTIES_STATUS,
  LIVE_MATCH_STATS_PATH,
  LIVE_MATCH_UPCOMING_STATUS,
  OPPONENT_GOAL,
  PENALTY,
  REAL_TIME_LIVE_MATCH_DATABASE,
  RED_CARD,
  SECOND_EXTRA_TIME,
  SECOND_HALF_KICK_OFF,
  SUBSTITUTION,
  TEXT_POST,
  VAR_CHECK,
  YELLOW_CARD,
} from "../../utils/constants/names";
import { customSeoConfig } from "../../utils/helpers/helpers";
import {
  convertTo24HrTime,
  convertToDateWithDayFirst,
  convertToDateWithMonthFirst,
} from "../../utils/helpers/dateAndTime";
import {
  LiveMatchHero,
  LiveMatchHeroSlider,
} from "../../components/common/slider/HeroSlider";

import { database } from "../../utils/helpers/firebaseConfig";
import LoadingSpinner from "../../components/common/LoadingSpinner";
import Image from "next/legacy/image";
import {
  addedTimeIcon,
  blurDataURL,
  calendarIcon,
  extraTimeIcon,
  fullTimeIcon,
  halfTimeIcon,
  kotokoGoalIcon,
  opponentGoalIcon,
  penaltyIcon,
  pinIcon,
  redCardIcon,
  refreeIcon,
  refreeWhistleIcon,
  seasonIcon,
  stadiumIcon,
  substitutionIcon,
  timerIcon,
  toDoListIcon,
  trophyIcon,
  varReplayIcon,
  yellowCardIcon,
} from "../../utils/constants/images";
import {
  LineupNoData,
  LiveMatchTabNoData,
} from "../../components/common/NoData";

import RedDividerH from "../../components/common/RedDividerH";
import PlayerFullName from "../../components/elements/liveMatch/PlayerFullName";
import TeamName from "../../components/elements/fixtures/TeamName";
import RedCardEvent from "../../components/elements/liveMatch/RedCardEvent";
import GoalEvent from "../../components/elements/liveMatch/GoalCardEvent";
import TeamLogo from "../../components/elements/fixtures/TeamLogo";
import ShootoutIndicator from "../../components/elements/liveMatch/ShootoutIndicator";
import WebShareIcon from "../../components/common/icons/WebShareIcon";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

const liveMatchType = {
  FIRST_HALF_KICK_OFF: "Kickoff - First Half",
  HALF_TIME: "Half Time",
  SECOND_HALF_KICK_OFF: "Kickoff - Second Half",
  FULL_TIME: "Full Time",
  KOTOKO_GOAL: "Goooaaal",
  OPPONENT_GOAL: "Goal",
  PENALTY: "Penalty Given",
  RED_CARD: "Red Card",
  YELLOW_CARD: "Yellow Card",
  SUBSTITUTION: "Substitution",
  VAR_CHECK: "Var Check",
  FIRST_EXTRA_TIME: "Extra Time - First Half",
  SECOND_EXTRA_TIME: "Extra Time - Second Half",
  ADDED_TIME: "Added Time",
};

const liveMatchIcons = {
  FIRST_HALF_KICK_OFF: refreeWhistleIcon,
  HALF_TIME: halfTimeIcon,
  SECOND_HALF_KICK_OFF: refreeWhistleIcon,
  FULL_TIME: fullTimeIcon,
  KOTOKO_GOAL: kotokoGoalIcon,
  OPPONENT_GOAL: opponentGoalIcon,
  PENALTY: penaltyIcon,
  RED_CARD: redCardIcon,
  YELLOW_CARD: yellowCardIcon,
  SUBSTITUTION: substitutionIcon,
  VAR_CHECK: varReplayIcon,
  FIRST_EXTRA_TIME: extraTimeIcon,
  SECOND_EXTRA_TIME: extraTimeIcon,
  ADDED_TIME: addedTimeIcon,
};

const liveMatchIconsText = {
  FIRST_HALF_KICK_OFF: "Kickoff - First Half",
  HALF_TIME: "Half-time",
  SECOND_HALF_KICK_OFF: "Kickoff - Second Half",
  FULL_TIME: "Full-time",
  KOTOKO_GOAL: "Goooaaal",
  OPPONENT_GOAL: "Goal",
  PENALTY: "Penalty Given",
  RED_CARD: "Red Card",
  YELLOW_CARD: "Yellow Card",
  SUBSTITUTION: "Substitution",
  VAR_CHECK: "Var Check",
  FIRST_EXTRA_TIME: "Extra-time - First Half",
  SECOND_EXTRA_TIME: "Extra-time - Second Half",
  ADDED_TIME: "Added Time",
};

const LiveMatchDetail = ({ liveMatchDetail }) => {
  const liveMatchInfo = liveMatchDetail?.live_match;
  const liveMatchFixture = liveMatchInfo.fixture;
  const liveMatchImages = liveMatchDetail?.live_match_images;

  const liveMatchThumbnail = liveMatchImages[0].image;

  const [formattedDate, setFormattedDate] = useState("");
  const [formattedTime, setFormattedTime] = useState("");
  const [formattedMatchInfoDate, setFormattedMatchInfoDate] = useState("");

  useEffect(() => {
    setFormattedDate(
      convertToDateWithMonthFirst(liveMatchFixture.date_and_time)
    );
    setFormattedTime(convertTo24HrTime(liveMatchFixture.date_and_time));
    setFormattedMatchInfoDate(
      convertToDateWithDayFirst(liveMatchFixture.date_and_time)
    );
  }, [liveMatchFixture.date_and_time]);

  const liveMatchGallery = liveMatchImages.map(function (item, index) {
    return item.image;
  });

  const liveMatchTextCommentary = ref(
    database,
    `${REAL_TIME_LIVE_MATCH_DATABASE}/${LIVE_MATCH_COMMENTARY_PATH}/fixture_${liveMatchFixture.id}`
  );

  const liveMatchLineUps = ref(
    database,
    `${REAL_TIME_LIVE_MATCH_DATABASE}/${LIVE_MATCH_LINE_UPS_PATH}/fixture_${liveMatchFixture.id}`
  );

  const liveMatchStats = ref(
    database,
    `${REAL_TIME_LIVE_MATCH_DATABASE}/${LIVE_MATCH_STATS_PATH}/fixture_${liveMatchFixture.id}`
  );

  const liveMatchFixtureInfo = ref(
    database,
    `${REAL_TIME_LIVE_MATCH_DATABASE}/${LIVE_MATCH_FIXTURES_PATH}/fixture_${liveMatchFixture.id}`
  );

  const [textCommentarySnapshots, textCommentaryLoading, textCommentaryError] =
    useList(liveMatchTextCommentary);

  const [lineupSnapshots, lineupLoading, lineupError] =
    useList(liveMatchLineUps);

  const [fixtureStatsSnapshots, fixtureStatsLoading, fixtureStatsError] =
    useList(liveMatchStats);

  const [fixtureInfoSnapshots, fixtureInfoLoading, fixtureInfoError] =
    useList(liveMatchFixtureInfo);

  const homeTeamLineUp = lineupSnapshots[2]?.val()
    ? lineupSnapshots[2]?.val()
    : [];
  const homeTeamSubstitutes = lineupSnapshots[3]?.val()
    ? lineupSnapshots[3]?.val()
    : [];
  const awayTeamLineUp = lineupSnapshots[0]?.val()
    ? lineupSnapshots[0]?.val()
    : [];
  const awayTeamSubstitutes = lineupSnapshots[1]?.val()
    ? lineupSnapshots[1]?.val()
    : [];

  const liveMatchFixtureStatus = fixtureInfoSnapshots[12]?.val();
  const fixtureCompetitionStatus = fixtureInfoSnapshots[10]?.val();
  const fixtureMinutePlayed = fixtureInfoSnapshots[23]?.val();
  const fixtureDateTime = liveMatchFixture.date_and_time;

  const liveMatchHomeTeamScore = fixtureInfoSnapshots[19]?.val();
  const liveMatchAwayTeamScore = fixtureInfoSnapshots[5]?.val();
  const homeTeamGoalScorers = fixtureInfoSnapshots[14]?.val()
    ? fixtureInfoSnapshots[14]?.val()
    : [];
  const homeTeamRedCards = fixtureInfoSnapshots[18]?.val()
    ? fixtureInfoSnapshots[18]?.val()
    : [];

  const awayTeamGoalScorers = fixtureInfoSnapshots[0]?.val()
    ? fixtureInfoSnapshots[0]?.val()
    : [];
  const awayTeamRedCards = fixtureInfoSnapshots[4]?.val()
    ? fixtureInfoSnapshots[4]?.val()
    : [];

  const homeTeamGoalScorersAndRedCards =
    homeTeamGoalScorers.concat(homeTeamRedCards);

  const awayTeamGoalScorersAndRedCards =
    awayTeamGoalScorers.concat(awayTeamRedCards);

  const homeTeamPenaltyShootout = fixtureInfoSnapshots[17]?.val()
    ? fixtureInfoSnapshots[17]?.val()
    : [];

  const awayTeamPenaltyShootout = fixtureInfoSnapshots[3]?.val()
    ? fixtureInfoSnapshots[3]?.val()
    : [];

  const homeTeamStats = fixtureStatsSnapshots[1]?.val()
    ? fixtureStatsSnapshots[1]?.val()
    : "";

  const awayTeamStats = fixtureStatsSnapshots[0]?.val()
    ? fixtureStatsSnapshots[0]?.val()
    : "";

  const stats = [
    {
      name: "Possession",
      home_team: homeTeamStats?.possession
        ? homeTeamStats?.possession + "%"
        : "0%",
      away_team: awayTeamStats?.possession
        ? awayTeamStats?.possession + "%"
        : "0%",
    },
    {
      name: "Total Shots",
      home_team: homeTeamStats?.total_shots ? homeTeamStats?.total_shots : "0",
      away_team: awayTeamStats?.total_shots ? awayTeamStats?.total_shots : "0",
    },
    {
      name: "Shots on Target",
      home_team: homeTeamStats?.shots_on_target
        ? homeTeamStats?.shots_on_target
        : "0",
      away_team: awayTeamStats?.shots_on_target
        ? awayTeamStats?.shots_on_target
        : "0",
    },
    {
      name: "Offsides",
      home_team: homeTeamStats?.offsides ? homeTeamStats?.offsides : "0",
      away_team: awayTeamStats?.offsides ? awayTeamStats?.offsides : "0",
    },
    {
      name: "Corners",
      home_team: homeTeamStats?.corners ? homeTeamStats?.corners : "0",
      away_team: awayTeamStats?.corners ? awayTeamStats?.corners : "0",
    },
    {
      name: "Total Fouls",
      home_team: homeTeamStats?.total_fouls ? homeTeamStats?.total_fouls : "0",
      away_team: awayTeamStats?.total_fouls ? awayTeamStats?.total_fouls : "0",
    },
    {
      name: "Yellow Cards",
      home_team: homeTeamStats?.yellow_cards
        ? homeTeamStats?.yellow_cards
        : "0",
      away_team: awayTeamStats?.yellow_cards
        ? awayTeamStats?.yellow_cards
        : "0",
    },
    {
      name: "Red Cards",
      home_team: homeTeamStats?.red_cards ? homeTeamStats?.red_cards : "0",
      away_team: awayTeamStats?.red_cards ? awayTeamStats?.red_cards : "0",
    },
  ];

  // const socialShareTitle = `${liveMatchFixture.home_team} vs ${liveMatchFixture.away_team} - ${liveMatchFixture.competition} Live Updates`;
  const socialShareTitle = `Match Center: ${liveMatchFixture.home_team} vs ${liveMatchFixture.away_team}`;
  const socialShareCaption = `A minute-by-minute live updates of the ${liveMatchFixture.competition} match between ${liveMatchFixture.home_team} vs ${liveMatchFixture.away_team} at ${liveMatchFixture.venue} in ${liveMatchFixture.city} on ${formattedDate}.`;

  return (
    <>
      {customSeoConfig(
        socialShareTitle,
        socialShareCaption,
        liveMatchThumbnail,
        liveMatchInfo.public_url,
        "article"
      )}

      <NoTextNavBackground />

      {liveMatchImages.length > 1 ? (
        <LiveMatchHeroSlider
          gallery={liveMatchGallery}
          matchStatus={liveMatchFixtureStatus}
          loadingFixtureInfo={fixtureInfoLoading}
          minutesPlayed={fixtureMinutePlayed}
          fixtureDateTime={fixtureDateTime}
        />
      ) : (
        <LiveMatchHero
          image={liveMatchGallery}
          matchStatus={liveMatchFixtureStatus}
          loadingFixtureInfo={fixtureInfoLoading}
          minutesPlayed={fixtureMinutePlayed}
          fixtureDateTime={fixtureDateTime}
        />
      )}

      <div className="bg-gray-900 px-6 sm:px-6 md:px-0 lg:px-0 xl:px-0 2xl:px-0 max-w-prose mx-auto rounded-br-md rounded-bl-md">
        <div className="flex justify-end items-end max-w-prose mx-auto pt-5 sm:mr-6">
          <WebShareIcon
            title={socialShareTitle}
            caption={socialShareCaption}
            url={liveMatchInfo.public_url}
          />
        </div>
        <div className="pb-16">
          <div className="flex flex-col justify-center items-center">
            <p className="text-xs sm:text-sm text-center text-gray-50 pt-4 sm:pt-6 w-[70vw] truncate text-ellipsis">
              {liveMatchFixture.competition}
            </p>
            <p className="text-xs sm:text-sm text-center text-gray-400 pb-2 w-[70vw] truncate text-ellipsis">
              {liveMatchFixture.week_or_round_number}
            </p>

            <p className="text-[10px] sm:text-xs text-center text-gray-50 pb-6 w-[70vw] truncate text-ellipsis">
              {fixtureCompetitionStatus ? (
                `(${fixtureCompetitionStatus})`
              ) : (
                <></>
              )}
            </p>
          </div>

          {/* PENALTY SHOOTOUT */}
          <div className="flex justify-center items-center space-x-10 sm:space-x-60 md:space-x-60 lg:space-x-60 xl:space-x-60 2xl:space-x-60 pb-4">
            <div className="w-32">
              {(liveMatchFixtureStatus === LIVE_MATCH_PENALTIES_STATUS ||
                liveMatchFixtureStatus === LIVE_MATCH_FULL_TIME_STATUS) &&
              (homeTeamPenaltyShootout.length !== 0 ||
                awayTeamPenaltyShootout.length !== 0) ? (
                homeTeamPenaltyShootout.map((shootout, key) => {
                  return <ShootoutIndicator key={key} score={shootout} />;
                })
              ) : (
                <></>
              )}
            </div>

            <div className="w-32">
              {(liveMatchFixtureStatus === LIVE_MATCH_PENALTIES_STATUS ||
                liveMatchFixtureStatus === LIVE_MATCH_FULL_TIME_STATUS) &&
              (homeTeamPenaltyShootout.length !== 0 ||
                awayTeamPenaltyShootout.length !== 0) ? (
                awayTeamPenaltyShootout.map((shootout, key) => {
                  return <ShootoutIndicator key={key} score={shootout} />;
                })
              ) : (
                <></>
              )}
            </div>
          </div>

          <div className="flex flex-col justify-center items-center inset-0 mr-10 ml-10 space-y-2">
            <div className="flex justify-between items-start">
              <div className="flex flex-col justify-center items-center space-y-2">
                <TeamLogo teamLogo={liveMatchFixture.home_team_logo} />

                {liveMatchFixtureStatus === LIVE_MATCH_UPCOMING_STATUS ? (
                  <>
                    <TeamName teamName={liveMatchFixture.home_team} />
                  </>
                ) : (
                  <></>
                )}
              </div>

              <div className="flex flex-col justify-base items-center px-6 sm:px-16 text-center">
                {liveMatchFixtureStatus === LIVE_MATCH_UPCOMING_STATUS ? (
                  <div className="text-xs inline-flex justify-center items-center pt-4 sm:pt-16">
                    <p className="text-gray-400  text-xl sm:text-3xl">VS</p>
                  </div>
                ) : (
                  <div className="text-xs inline-flex justify-center items-center pt-4 sm:pt-10">
                    <p className="text-white font-semibold text-3xl sm:text-6xl">
                      {liveMatchHomeTeamScore}
                    </p>

                    <p className="text-gray-400 px-5 text-base sm:text-7xl">
                      -
                    </p>

                    <p className="text-white font-semibold text-3xl sm:text-6xl">
                      {liveMatchAwayTeamScore}
                    </p>
                  </div>
                )}
              </div>
              <div className="flex flex-col justify-center items-center space-y-2">
                <TeamLogo teamLogo={liveMatchFixture.away_team_logo} />

                {liveMatchFixtureStatus === LIVE_MATCH_UPCOMING_STATUS ? (
                  <>
                    <TeamName teamName={liveMatchFixture.away_team} />
                  </>
                ) : (
                  <></>
                )}
              </div>
            </div>

            <div className="flex justify-between space-x-10 sm:space-x-60 md:space-x-60 lg:space-x-60 xl:space-x-60 2xl:space-x-60">
              <div className="w-32">
                {liveMatchFixtureStatus === LIVE_MATCH_UPCOMING_STATUS ? (
                  <></>
                ) : (
                  homeTeamGoalScorersAndRedCards.map((event, key) => {
                    return (
                      <>
                        {event.type === LIVE_MATCH_GOAL_EVENT ? (
                          <>
                            <GoalEvent
                              key={key}
                              playerNameAndTime={event.playerNameAndTime}
                            />
                          </>
                        ) : (
                          <>
                            <RedCardEvent
                              key={key}
                              playerNameAndTime={event.playerNameAndTime}
                            />
                          </>
                        )}
                      </>
                    );
                  })
                )}
              </div>

              <div className="w-32">
                {liveMatchFixtureStatus === LIVE_MATCH_UPCOMING_STATUS ? (
                  <></>
                ) : (
                  awayTeamGoalScorersAndRedCards.map((event, key) => {
                    return (
                      <>
                        {event.type === LIVE_MATCH_GOAL_EVENT ? (
                          <>
                            <GoalEvent
                              key={key}
                              playerNameAndTime={event.playerNameAndTime}
                            />
                          </>
                        ) : (
                          <>
                            <RedCardEvent
                              key={key}
                              playerNameAndTime={event.playerNameAndTime}
                            />
                          </>
                        )}
                      </>
                    );
                  })
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="block w-full text-base sm:text-sm mt-8">
        <Tab.Group>
          <Tab.List className="flex justify-center items-center space-x-8 overflow-x-auto pl-44 sm:pl-0">
            <Tab
              className={({ selected }) =>
                classNames(
                  "whitespace-nowrap px-3 py-2 font-medium text-sm rounded-md focus:outline-none mb-6",
                  selected
                    ? "bg-red-100 text-red-700 dark:bg-green-100 dark:text-green-700 active:border-none"
                    : "text-gray-500 hover:text-gray-700"
                )
              }
            >
              Live Feed
            </Tab>
            <Tab
              className={({ selected }) =>
                classNames(
                  "whitespace-nowrap px-3 py-2 font-medium text-sm rounded-md focus:outline-none mb-6",
                  selected
                    ? "bg-red-100 text-red-700 dark:bg-green-100 dark:text-green-700 active:border-none"
                    : "text-gray-500 hover:text-gray-700"
                )
              }
            >
              Stats
            </Tab>
            <Tab
              className={({ selected }) =>
                classNames(
                  "whitespace-nowrap px-3 py-2 font-medium text-sm rounded-md focus:outline-none mb-6",
                  selected
                    ? "bg-red-100 text-red-700 dark:bg-green-100 dark:text-green-700 active:border-none"
                    : "text-gray-500 hover:text-gray-700"
                )
              }
            >
              Line-ups
            </Tab>
            <Tab
              className={({ selected }) =>
                classNames(
                  "whitespace-nowrap px-3 py-2 font-medium text-sm rounded-md focus:outline-none mb-6",
                  selected
                    ? "bg-red-100 text-red-700 dark:bg-green-100 dark:text-green-700 active:border-none"
                    : "text-gray-500 hover:text-gray-700"
                )
              }
            >
              Match Info
            </Tab>
          </Tab.List>
          <Tab.Panels>
            {/* TEXT COMMENTARY */}
            <Tab.Panel>
              <div className="px-6 sm:px-6 md:px-0 lg:px-0 xl:px-0 2xl:px-0 max-w-prose mx-auto">
                {textCommentaryError && (
                  <strong>Error: Oops!!! Something went wrong.</strong>
                )}

                {textCommentaryLoading && <LoadingSpinner />}

                {!textCommentaryLoading && textCommentarySnapshots && (
                  <>
                    {[...textCommentarySnapshots]
                      .reverse()
                      .map((commentary, key) => {
                        const value = commentary.val();

                        if (value.type === IMAGE_POST) {
                          return (
                            <div
                              key={key}
                              className="group relative transform transition duration-500 hover:scale-105 mb-10"
                            >
                              <div className="relative w-full bg-white aspect-w-1 aspect-h-1 rounded-md hover:opacity-75 overflow-hidden sm:aspect-w-2 sm:aspect-h-3">
                                <Image
                                  src={value?.image}
                                  alt="Live Match Image"
                                  objectFit="cover"
                                  layout="fill"
                                  quality={100}
                                  placeholder="blur"
                                  blurDataURL={blurDataURL}
                                  className="absolute w-full h-full object-center object-cover"
                                />

                                <div
                                  aria-hidden="true"
                                  className="bg-gradient-to-b from-transparent to-black opacity-100 absolute inset-0"
                                ></div>
                                <div className="p-6 flex items-end justify-center absolute inset-0">
                                  <div>
                                    <h1 className="mt-2 mb-0 text-justify  text-white text-xs lg:text-sm xl:text-sm 2xl:text-base break-words">
                                      <span> {value?.textCommentary}</span>
                                    </h1>
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        }

                        if (value.type === TEXT_POST) {
                          return (
                            <div
                              key={key}
                              className="sticky w-full max-w-xl px-8 py-8 mx-auto space-y-4 mb-10 bg-white border rounded-md shadow-lg"
                            >
                              <p>{value.textCommentary}</p>
                            </div>
                          );
                        }

                        if (
                          value.type === FIRST_HALF_KICK_OFF ||
                          value.type === HALF_TIME ||
                          value.type === SECOND_HALF_KICK_OFF ||
                          value.type === FULL_TIME ||
                          value.type === KOTOKO_GOAL ||
                          value.type === OPPONENT_GOAL ||
                          value.type === PENALTY ||
                          value.type === RED_CARD ||
                          value.type === YELLOW_CARD ||
                          value.type === SUBSTITUTION ||
                          value.type === VAR_CHECK ||
                          value.type === FIRST_EXTRA_TIME ||
                          value.type === SECOND_EXTRA_TIME ||
                          value.type === ADDED_TIME
                        ) {
                          return (
                            <div
                              key={key}
                              className={classNames(
                                value.type === KOTOKO_GOAL
                                  ? "bg-red-100 dark:bg-green-100"
                                  : value.type === OPPONENT_GOAL
                                  ? "bg-gray-400"
                                  : "bg-white",
                                "sticky w-full max-w-xl px-8 py-8 mx-auto space-y-4 mb-10 border rounded-md shadow-lg"
                              )}
                            >
                              <h2 className="space-y-1 text-2xl font-bold leading-none text-gray-900">
                                <span className="block text-xs font-bold mb-4">
                                  {value.minute === "1"
                                    ? `${value.minute} Min`
                                    : `${value.minute} Mins`}
                                </span>

                                <span className="flex items-center text-base space-x-2">
                                  <Image
                                    src={liveMatchIcons[value.type]}
                                    alt="Match Event Icon"
                                    objectFit="contain"
                                    width="24px"
                                    height="24px"
                                    quality={100}
                                  />
                                  <span>{liveMatchIconsText[value.type]}</span>
                                </span>
                                <div className="border-t border-black"></div>
                              </h2>
                              <p>{value.textCommentary}</p>
                            </div>
                          );
                        }

                        return (
                          <div
                            key={key}
                            className="sticky w-full max-w-xl px-8 py-8 mx-auto space-y-4 mb-10 bg-white border rounded-md shadow-md"
                          >
                            <h2 className="space-y-1 text-2xl font-bold leading-none text-gray-900">
                              <span className="block text-xs font-bold mb-2">
                                {value.minute === "1"
                                  ? `${value.minute} Min`
                                  : `${value.minute} Mins`}
                              </span>
                              <div className="border-t border-black"></div>
                            </h2>
                            <p>{value.textCommentary}</p>
                          </div>
                        );
                      })}
                  </>
                )}

                {!textCommentaryLoading &&
                  textCommentarySnapshots.length === 0 && (
                    <LiveMatchTabNoData
                      message={
                        "Text commentary will be available once the game starts."
                      }
                    />
                  )}
              </div>
            </Tab.Panel>

            {/* STATS */}
            <Tab.Panel>
              <div className="relative px-6 sm:px-6 md:px-0 lg:px-0 xl:px-0 2xl:px-0 max-w-prose mx-auto">
                {fixtureStatsError && (
                  <strong>Error: Oops!!! Something went wrong.</strong>
                )}

                {fixtureStatsLoading && <LoadingSpinner />}

                {!fixtureStatsLoading && stats && (
                  <>
                    <dl className="grid grid-cols-1 gap-5 sm:grid-cols-2 mb-6">
                      {stats.map((stat) => (
                        <div
                          key={stat.name}
                          className="px-4 py-8 bg-gray-900 shadow rounded-md overflow-hidden sm:p-6"
                        >
                          <p className="uppercase text-xs text-gray-400 mb-6 text-center">
                            {stat.name}
                          </p>

                          <div className="flex justify-between">
                            <div>
                              <dt className="text-sm font-medium text-gray-500 ">
                                <p
                                  className={classNames(
                                    liveMatchFixture.home_team ===
                                      "Asante Kotoko" ||
                                      liveMatchFixture.home_team === "Kotoko"
                                      ? "font-bold"
                                      : "font-normal",
                                    "text-ellipsis truncate w-28"
                                  )}
                                >
                                  {liveMatchFixture.home_team}
                                </p>
                              </dt>
                              <dd className="mt-2 text-3xl tracking-tight font-semibold text-gray-50">
                                {stat.home_team}
                              </dd>
                            </div>

                            <div>
                              <dt className="text-sm font-medium text-gray-500">
                                <p
                                  className={classNames(
                                    liveMatchFixture.away_team ===
                                      "Asante Kotoko" ||
                                      liveMatchFixture.away_team === "Kotoko"
                                      ? "font-bold"
                                      : "font-normal",
                                    "text-ellipsis truncate w-28"
                                  )}
                                >
                                  {liveMatchFixture.away_team}
                                </p>
                              </dt>
                              <dd className="mt-2 text-3xl tracking-tight font-semibold text-gray-50 flex justify-between">
                                {stat.away_team}
                              </dd>
                            </div>
                          </div>
                        </div>
                      ))}
                    </dl>
                  </>
                )}
              </div>
            </Tab.Panel>
            {/* LINE-UPS */}
            <Tab.Panel>
              <div className="px-4 sm:px-4 md:px-0 lg:px-0 xl:px-0 2xl:px-0 max-w-prose mx-auto">
                {lineupError && (
                  <strong>Error: Oops!!! Something went wrong.</strong>
                )}

                {lineupLoading && <LoadingSpinner />}

                {!lineupLoading &&
                homeTeamLineUp.length !== 0 &&
                awayTeamLineUp.length !== 0 ? (
                  <>
                    <div>
                      <div className="flex justify-between space-x-4">
                        <div>
                          <h1 className="font-semibold mb-2 text-lg">
                            {liveMatchFixture?.home_team}
                            <RedDividerH />
                          </h1>

                          <div>
                            {homeTeamLineUp.map((player, key) => (
                              <PlayerFullName key={key} fullName={player} />
                            ))}
                          </div>
                        </div>
                        <div className="">
                          <h1 className="font-semibold mb-2 text-lg">
                            {liveMatchFixture?.away_team}
                            <RedDividerH />
                          </h1>

                          <div>
                            {awayTeamLineUp.map((player, key) => (
                              <PlayerFullName key={key} fullName={player} />
                            ))}
                          </div>
                        </div>
                      </div>
                      <div>
                        <div className="text-gray-500 text-xs sm:text-sm text-center mt-8 mb-4">
                          Subsitutes
                        </div>
                        <div className="flex justify-between space-x-4">
                          <div>
                            <div>
                              {homeTeamSubstitutes.map((player, key) => (
                                <PlayerFullName key={key} fullName={player} />
                              ))}
                            </div>
                          </div>
                          <div className="">
                            <div>
                              {awayTeamSubstitutes.map((player, key) => (
                                <PlayerFullName key={key} fullName={player} />
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  <LineupNoData />
                )}
              </div>
            </Tab.Panel>
            {/* MATCH INFO */}
            <Tab.Panel>
              <div className="px-6 sm:px-6 md:px-0 lg:px-0 xl:px-0 2xl:px-0 max-w-prose mx-auto">
                <div className="mb-6">
                  <h1 className="font-semibold text-lg">Match Info</h1>
                  <RedDividerH />
                </div>

                <div className="flex flex-col space-y-6">
                  <div className="inline-flex space-x-4 items-center">
                    <Image
                      src={seasonIcon}
                      alt="Season Icon"
                      objectFit="contain"
                      width="30px"
                      height="30px"
                      quality={100}
                    />

                    <p className="text-sm sm:text-base">
                      {liveMatchFixture?.season}
                    </p>
                  </div>

                  <div className="inline-flex space-x-4 items-center">
                    <Image
                      src={trophyIcon}
                      alt="Trophy Icon"
                      objectFit="contain"
                      width="30px"
                      height="30px"
                      quality={100}
                    />

                    <p className="text-sm sm:text-base">
                      {liveMatchFixture?.competition}
                    </p>
                  </div>

                  <div className="inline-flex space-x-4 items-center">
                    <Image
                      src={toDoListIcon}
                      alt="Trophy Icon"
                      objectFit="contain"
                      width="30px"
                      height="30px"
                      quality={100}
                    />

                    <p className="text-sm sm:text-base">
                      {liveMatchFixture?.week_or_round_number}
                    </p>
                  </div>

                  <div className="inline-flex space-x-4 items-center">
                    <Image
                      src={calendarIcon}
                      alt="Calender Icon"
                      objectFit="contain"
                      width="30px"
                      height="30px"
                      quality={100}
                    />

                    <p className="text-sm sm:text-base">
                      {formattedMatchInfoDate}
                    </p>
                  </div>

                  <div className="inline-flex space-x-4 items-center">
                    <Image
                      src={timerIcon}
                      alt="Time Icon"
                      objectFit="contain"
                      width="30px"
                      height="30px"
                      quality={100}
                    />

                    <p className="text-sm sm:text-base">{formattedTime}</p>
                  </div>

                  <div className="inline-flex space-x-4 items-center">
                    <Image
                      src={refreeIcon}
                      alt="Referee Icon"
                      objectFit="contain"
                      width="30px"
                      height="30px"
                      quality={100}
                    />

                    <p className="text-sm sm:text-base">
                      {liveMatchFixture?.referee_name}
                    </p>
                  </div>

                  <div className="inline-flex space-x-4 items-center">
                    <Image
                      src={stadiumIcon}
                      alt="Stadium Icon"
                      objectFit="contain"
                      width="30px"
                      height="30px"
                      quality={100}
                    />

                    <p className="text-sm sm:text-base">
                      {liveMatchFixture?.venue}
                    </p>
                  </div>

                  <div className="inline-flex space-x-4 items-center">
                    <Image
                      src={pinIcon}
                      alt="Location Icon"
                      objectFit="contain"
                      width="30px"
                      height="30px"
                      quality={100}
                    />

                    <p className="text-sm sm:text-base">
                      {liveMatchFixture?.city}
                    </p>
                  </div>
                </div>
              </div>
            </Tab.Panel>
          </Tab.Panels>
        </Tab.Group>
      </div>
    </>
  );
};

export default LiveMatchDetail;

export const getServerSideProps = async (context) => {
  const guestUserToken = process.env.GUSET_USER_TOKEN
    ? process.env.GUSET_USER_TOKEN
    : GUEST_TOKEN;

  const data = await getSession(context);

  const userToken = data?.accessToken ? data.accessToken : guestUserToken;

  const matchSlug = context.query.slug;

  const { req, res } = context;

  res.setHeader(
    "Cache-Control",
    "public, s-maxage=86400, stale-while-revalidate=60"
  );

  const matchDetail = await apiWrapper.get(
    kLiveMatchDetailURL + `?slug=${matchSlug}`,
    userToken
  );

  return {
    props: {
      liveMatchDetail: matchDetail?.data,
    },
  };
};
